import React, { useEffect, useState, useRef } from 'react';
import {
  DatePicker,
  Select,
  Button,
  Form,
  Cascader,
  Input,
  Upload,
  message,
  Radio,
  Checkbox,
  Spin,
} from 'antd';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/zh_CN';
import './index.scss';
import { request } from '@/fetch/core';
import { HttpStatusCode } from '@/fetch/core/constant';
import { cloneDeep } from 'lodash';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import {
  clstagStr,
  formatCommonCategory,
  formatCommonCategoryChild,
} from '@/utils/util';
import { showModal } from '../Common/Modal';
import PreviewList from '../PreviewList';
import { CLSTAG } from '@/utils/constant';
import { enumTranslator } from '@/utils';
import { sendGlobalEvent } from '../../utils/emit';
import { GlobalEventName } from '@/utils/constant';
const { RangePicker } = DatePicker;
export interface FormProps {
  config: FormConfig;
  enableSelectAllField?: string;
  onSearch?: AnyFunc;
  onReset?: AnyFunc;
  onChange?: AnyFunc;
  onFieldChange?: AnyFunc;
  onBlur?: AnyFunc;
  onValuesChange?: AnyFunc;
  layout?: 'horizontal' | 'vertical' | 'inline';
  defaultValue?: {
    [key in string]: any;
  };
  getFormInstance?: (formRef: any) => any;
  className?: string;
  labelAlign?: 'left' | 'right' | undefined;
  size?: 'middle' | 'large';
  disabled?: boolean;
  clstag?: CLSTAG;
}

const CommonForm = (props: FormProps) => {
  const {
    config,
    onSearch,
    onReset,
    onChange,
    onFieldChange,
    layout = 'inline',
    defaultValue,
    getFormInstance,
    className,
    onValuesChange,
    labelAlign,
    enableSelectAllField,
    size,
    disabled,
    clstag,
  } = props;
  const customValidatorRef = useRef<any>([]);
  const [form] = Form.useForm();
  const [formData, setFormData] = useState<any>(null);
  const [allSelected, setAllSelected] = useState(true);
  const [fileListMap, setFileListMap] = useState<any>({});
  const [showUploading, setShowUploading] = useState(false);
  const [uploadKey, setUploadKey] = useState<any>('init');
  useEffect(() => {
    config?.data?.forEach((item) => {
      config?.linkRules?.forEach((rules) => {
        if (item.fieldName === rules.linkFieldName) {
          customValidatorRef.current.push({
            fieldName: item.fieldName,
            validatorRules: item.validatorRules,
          });
        }
      });
    });
  }, []);

  useEffect(() => {
    const formatData = formatFormData(cloneDeep(config));

    setFormData(formatData);
  }, [JSON.stringify(config)]);

  useEffect(() => {
    config?.data?.forEach((field: FieldItem) => {
      if (field.cmpType == 'upload' || field.cmpType == 'preview') {
        fileListMap[field.fieldName] = field.defaultFileList || [];
      }
    });
    setFileListMap({
      ...fileListMap,
    });
  }, [config]);
  // 限制当天以后的日期不可选
  const disabledDate = (current: any) => {
    return current && current >= dayjs().endOf('days');
  };

  /**
   * 上传附件
   * @param {any} option
   */
  const customRequest = (option: any, fieldName: string) => {
    if (!option?.file) {
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(option.file);
    reader.onloadend = async (e: any) => {
      option.onSuccess();
      const formData = new FormData();
      option.file && formData.append('file', option.file);
      const requestParam: RequestOptions = {
        method: 'POST',
        path: option?.action || '/monitor/web/jira/jira-attachment', // jira上传附件
        contentType: 'formData',
        body: formData,
      };
      const res: any = await request(requestParam);
      if (res.code != HttpStatusCode.Success) {
        message.error(res.message);
      } else {
        onFieldChange &&
          onFieldChange(
            fieldName,
            {
              uid: option.file?.uid,
              type: option.file?.type?.startsWith('video')
                ? 'video'
                : option.file?.type?.startsWith('image')
                  ? 'image'
                  : option.file?.type,
              name: option.file?.name,
              fileKey: res?.data?.fileKey,
            },
            'add',
          );
        setFileListMap({
          ...fileListMap,
        });
      }
      sendGlobalEvent(GlobalEventName.JDX_FILE_UPLOAD_STATUS, {
        fieldName: fieldName,
        uploading: false,
      });
      setShowUploading(false);
    };
  };
  const onAllSelectChange = (e: CheckboxChangeEvent) => {
    if (enableSelectAllField) {
      setAllSelected(e.target.checked);
      const field = formData?.data?.find(
        (item: any) => item.fieldName === enableSelectAllField,
      );
      if (e.target.checked) {
        form.setFieldsValue({
          [enableSelectAllField]: formatCommonCategory(field.data),
        });
      } else {
        form.setFieldsValue({
          [enableSelectAllField]: [],
        });
      }
    }
  };

  const renderFieldItem = (dataItem: FieldItem) => {
    const {
      fieldName,
      cmpType,
      data,
      placeholder,
      label,
      multiple,
      validatorRules = [],
      type = 'image/*,video/*',
      max = 5,
      hasSearch,
      maxLength,
      allowClear,
      changeOnSelect,
      disabled,
      showSelectAll,
      mode,
      actionUrl,
      maxSize,
      showUploadList = true,
      showPreviewName = false,
      valueToLabel,
      valueToLabelEnumGroupKey,
      valueToLabelCondition,
    } = dataItem;

    if (
      valueToLabel &&
      valueToLabelCondition &&
      defaultValue &&
      defaultValue[fieldName]
    ) {
      const fieldDefaultValue = defaultValue[fieldName];
      if (valueToLabelEnumGroupKey) {
        const value = enumTranslator(
          valueToLabelEnumGroupKey,
          fieldDefaultValue,
        );
        return <label className='value-text'>{value}</label>;
      } else {
        return <label className='value-text'>{fieldDefaultValue}</label>;
      }
    }
    switch (cmpType) {
      case 'input':
        return (
          <Input
            placeholder={placeholder ?? ''}
            type={type ?? 'text'}
            allowClear={typeof allowClear === 'undefined' ? true : allowClear}
          />
        );
      case 'radioGroup':
        return <Radio.Group options={data} />;
      case 'textarea':
        return (
          <Input.TextArea
            placeholder={placeholder ?? ''}
            autoSize={{ minRows: 2, maxRows: 6 }}
            onBlur={(e) => {
              onFieldChange && onFieldChange(fieldName, e.target.value);
            }}
            maxLength={maxLength}
            showCount={{
              formatter: ({ value, count, maxLength }) =>
                `${count}/${maxLength}`,
            }}
          />
        );
      case 'select':
        return (
          <Select
            placeholder={placeholder ?? ''}
            options={data ?? []}
            showSearch={!!hasSearch}
            allowClear={typeof allowClear === 'undefined' ? true : allowClear}
            filterOption={(inputValue, option) => {
              return String(option?.label)?.includes(inputValue);
            }}
            getPopupContainer={(trigger) => trigger.parentNode}
            style={{
              minWidth: '150px',
            }}
            onChange={(val) => {
              onFieldChange && onFieldChange(fieldName, val);
            }}
            mode={mode}
            disabled={disabled}
          />
        );
      case 'rangeTime':
        return (
          <RangePicker
            locale={locale}
            showTime
            format='YYYY-MM-DD HH:mm:ss'
            disabledDate={disabledDate}
          />
        );
      case 'dateTime':
        return (
          <DatePicker
            allowClear={typeof allowClear === 'undefined' ? true : allowClear}
            locale={locale}
            showTime
            format='YYYY-MM-DD HH:mm:ss'
            disabledDate={disabledDate}
            onChange={(val) => {
              onFieldChange &&
                onFieldChange(fieldName, val?.format('YYYY-MM-DD HH:mm:ss'));
            }}
            disabled={disabled}
          />
        );
      case 'cascader':
        return (
          <Cascader
            fieldNames={{ label: 'name', value: 'id', children: 'child' }}
            options={data}
            placeholder={placeholder ?? ''}
            changeOnSelect={!!changeOnSelect}
            size={'middle'}
            style={{ width: '100%' }}
            maxTagCount={2}
            onChange={(val: any) => {
              onChange && onChange(val, fieldName);
            }}
            showSearch={!!hasSearch}
            allowClear={!!allowClear}
            multiple={multiple}
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
            showCheckedStrategy={Cascader.SHOW_CHILD}
            dropdownRender={(menus) => {
              return (
                <>
                  {showSelectAll && (
                    <Checkbox
                      className='select-all'
                      onChange={onAllSelectChange}
                      checked={allSelected}
                    >
                      全部
                    </Checkbox>
                  )}
                  {menus}
                </>
              );
            }}
          />
        );
      case 'upload':
        return (
          <div className='upload-wrapper'>
            <PreviewList
              fill={true}
              fileList={fileListMap[fieldName] || []}
              onDelete={(file: any) => {
                fileListMap[fieldName] = fileListMap[fieldName]?.filter(
                  (item: any) =>
                    item.fileKey != file.fileKey || item.uid != file.uid,
                );
                onFieldChange && onFieldChange(fieldName, file, 'delete');
                setFileListMap({
                  ...fileListMap,
                });
                setUploadKey(Date.now());
              }}
              onPreview={(file: any) => {}}
            />
            {showUploading && <Spin tip='正在上传中......' />}
            <Upload
              listType='picture-card'
              accept={type}
              maxCount={max}
              key={uploadKey}
              action={actionUrl}
              showUploadList={showUploadList}
              onChange={(data: any) => {
                if (data.file.status === 'done') {
                  const url = URL.createObjectURL(data.file.originFileObj);
                  fileListMap[fieldName]?.push({
                    uid: data.file?.uid,
                    type: data.file?.type?.startsWith('video')
                      ? 'video'
                      : data.file?.type?.startsWith('image')
                        ? 'image'
                        : data.file?.type,
                    name: data.file?.name,
                    url: url,
                  });
                } else if (data.file.status === 'uploading') {
                  sendGlobalEvent(GlobalEventName.JDX_FILE_UPLOAD_STATUS, {
                    fieldName: fieldName,
                    uploading: true,
                  });
                  setShowUploading(true);
                }
              }}
              beforeUpload={(file, fileList) => {
                if (fileListMap[fieldName]?.length >= max) {
                  message.error(`最多支持上传${max}个文件！`);
                  return Upload.LIST_IGNORE;
                } else if (file.size > maxSize!) {
                  message.error('文件过大，上传失败！');
                  return Upload.LIST_IGNORE;
                }
                return file;
              }}
              customRequest={(option: any) => {
                customRequest(option, fieldName);
              }}
              onPreview={(file) => {
                showModal({
                  title: '图片预览',
                  width: 800,
                  footer: null,
                  className: 'upload-modal',
                  content: <img height={600} src={file.thumbUrl} />,
                });
              }}
              onRemove={(file: any) => {
                onFieldChange && onFieldChange(fieldName, file, 'delete');
              }}
            >
              +
            </Upload>
          </div>
        );
      case 'preview':
        return (
          <div className='preview-wrapper'>
            {fileListMap[fieldName] && fileListMap[fieldName].length > 0 ? (
              <PreviewList
                fill={true}
                fileList={fileListMap[fieldName] || []}
                onPreview={(file: any) => {}}
                showPreviewName={showPreviewName}
              />
            ) : (
              <label className='value-text'>{placeholder}</label>
            )}
          </div>
        );
      default:
        return null;
    }
  };
  const onFinish = async (values: any) => {
    const formValue = await form.validateFields();
    onSearch && onSearch(formValue);
  };

  /**
   * Formats the given form data by modifying certain properties.
   * @param {FormConfig}formData - The form configuration data.
   * @return {FieldItem}The formatted form data.
   */
  const formatFormData = (formData: FormConfig) => {
    return {
      ...formData,
      data: formData?.data?.map((item: FieldItem) => {
        if (item.hidden) {
          return {
            ...item,
            validatorRules: [{ required: false }],
          };
        }
        return item;
      }),
    };
  };

  /**
   * This function is triggered when there are changes in the fields.
   * It updates the visibility and validation rules based on the changed values.
   *
   * @param {Object} changedValues - The changed values of the fields.
   * @param {Object} allValues - All the values of the fields.
   */
  const onFieldsChange = (changedValues: any, allValues: any) => {
    const changedValue = changedValues[0];
    const changedName = changedValue?.name[0];

    // Iterate through the link rules in the form data
    for (const i of formData?.linkRules || []) {
      if (i.fieldName === changedName) {
        const { linkFieldName, type, visibleRule } = i;

        if (type.includes('visible') && visibleRule) {
          const { show } = visibleRule;

          // Find the link field in the form data
          const linkField = formData.data.find(
            (field: FieldItem) => field.fieldName === linkFieldName,
          );

          // Update the visibility of the link field
          linkField.hidden = !show.includes(changedValue.value);
          // Update the required validation rule of the link field
          customValidatorRef.current.forEach((item: any) => {
            if (linkField.fieldName === item.fieldName && !linkField.hidden) {
              linkField.validatorRules = cloneDeep(item.validatorRules);
            } else if (
              linkField.fieldName === item.fieldName &&
              linkField.hidden
            ) {
              if (linkField?.validatorRules) {
                linkField.validatorRules = [{ required: false }];
              }
            }
          });
        }
      }
    }
    // Update the form data
    setFormData({ ...formData });
  };

  useEffect(() => {
    getFormInstance && getFormInstance(form);
  }, []);

  useEffect(() => {
    if (enableSelectAllField) {
      const field = formData?.data?.find(
        (item: any) => item.fieldName === enableSelectAllField,
      );
      const valueArr = form.getFieldValue(enableSelectAllField);
      const fieldLength = formatCommonCategory(field?.data).length;
      if (valueArr && valueArr.length > 0 && valueArr.length < fieldLength) {
        setAllSelected(false);
      } else if (valueArr && valueArr.length === fieldLength) {
        setAllSelected(true);
      } else if (valueArr && valueArr.length === 0) {
        setAllSelected(false);
      }
    }
  }, [enableSelectAllField && form.getFieldValue(enableSelectAllField)]);

  return (
    <div className='form-view'>
      <Form
        colon={false}
        name='complex-form'
        labelAlign={labelAlign || 'right'}
        layout={layout}
        size={size ?? 'large'}
        form={form}
        initialValues={defaultValue || {}}
        autoComplete='off'
        onFieldsChange={onFieldsChange}
        onValuesChange={onValuesChange}
        className={className || ''}
        style={{
          alignItems: 'center',
        }}
        disabled={disabled}
      >
        {formData?.data?.map((item: FieldItem) => {
          return (
            <Form.Item
              labelCol={item.labelCol || { span: 6 }}
              wrapperCol={item.wrapperCol}
              key={item.fieldName.toString()}
              name={item.fieldName}
              label={
                item.showCounter
                  ? item.label +
                    '(' +
                    fileListMap[item.fieldName].length +
                    '/' +
                    item.max +
                    ')'
                  : item.label
              }
              help={item.expandNode}
              rules={item.validatorRules ?? []}
              hidden={!!item.hidden}
              className={'field_' + item.fieldName.toString()}
            >
              {renderFieldItem(item)}
            </Form.Item>
          );
        })}
        {onSearch && (
          <Button
            className='search-btn'
            type='primary'
            onClick={onFinish}
            clstag={clstag && clstagStr(clstag)}
          >
            查询
          </Button>
        )}
        {onReset && (
          <Button
            htmlType='button'
            className='reset-btn'
            onClick={() => {
              form.resetFields();
              onReset();
            }}
          >
            重置
          </Button>
        )}
      </Form>
    </div>
  );
};

export default React.memo(CommonForm);
